// TemporaryStoppagesList.js
import React, { useEffect, useState } from "react";
import axios from "../../../api/axios";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
} from "@mui/material";

import { CheckCircle } from "@mui/icons-material";

export default function TemporaryStoppagesList() {
  const [stoppages, setStoppages] = useState([]);
  const [message, setMessage] = useState(null);

  // Modal state
  const [open, setOpen] = useState(false);
  const [selectedStoppage, setSelectedStoppage] = useState(null);
  const [form, setForm] = useState({ actual_resumption_date: "", remarks: "" });
  const [file, setFile] = useState(null);

  useEffect(() => {
    fetchStoppages();
  }, []);

  const fetchStoppages = () => {
    axios
      .get("/temporary-stoppages")
      .then((res) => setStoppages(res.data))
      .catch((err) => console.error(err));
  };

  const handleResumeClick = (stoppage) => {
    setSelectedStoppage(stoppage);
    setForm({ actual_resumption_date: "", remarks: "" });
    setFile(null);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedStoppage(null);
  };

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedStoppage) return;

    const formData = new FormData();
    Object.entries(form).forEach(([k, v]) => formData.append(k, v));
    if (file) formData.append("resumption_authority_document", file);

    try {
      await axios.post(`/temporary-stoppages/${selectedStoppage.id}/resume`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMessage("Staff successfully resumed!");
      handleClose();
      fetchStoppages();
    } catch (error) {
      console.error(error);
      setMessage("Failed to resume staff. Please try again.");
    }
  };

  const getStatusChip = (status) => {
    switch (status) {
      case "Active":
        return <Chip label="🔴 Active" color="error" variant="outlined" />;
      case "Scheduled":
        return <Chip label="⏳ Scheduled" color="warning" variant="outlined" />;
      case "Resumed":
        return <Chip label="✅ Resumed" color="success" variant="outlined" />;
      default:
        return <Chip label={status} />;
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <div style={{ padding: "24px" }}>
        <h2 style={{ fontSize: "1.5rem", fontWeight: "600", marginBottom: "16px" }}>
          Temporary Stoppages
        </h2>

        <TableContainer component={Paper} sx={{ borderRadius: "12px", boxShadow: 2 }}>
          <Table size="small" stickyHeader sx={{ tableLayout: "fixed" }}>
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>PF No</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell>Expected Resumption</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Action</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {stoppages.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                    No stoppages found
                  </TableCell>
                </TableRow>
              ) : (
                stoppages.map((stop, index) => (
                  <TableRow key={stop.id} hover>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{stop.pfno}</TableCell>
                    <TableCell>{stop.reason}</TableCell>
                    <TableCell>{stop.start_date}</TableCell>
                    <TableCell>{stop.expected_resumption_date}</TableCell>
                    <TableCell>{getStatusChip(stop.status)}</TableCell>
                    <TableCell align="center">
                      {stop.status !== "Resumed" ? (
                        <Tooltip title="Resume Staff">
                          <IconButton color="success" onClick={() => handleResumeClick(stop)}>
                            <CheckCircle />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <span style={{ color: "#999" }}>—</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      <Footer />

      {/* Resume Staff Modal */}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>Resume Staff</DialogTitle>
        <DialogContent dividers>
          {selectedStoppage && (
            <div style={{ marginBottom: "16px" }}>
              <p>
                <strong>PF No:</strong> {selectedStoppage.pfno}
              </p>
              <p>
                <strong>Reason:</strong> {selectedStoppage.reason}
              </p>
              <p>
                <strong>Status:</strong> {selectedStoppage.status}
              </p>
              <p>
                <strong>Start:</strong> {selectedStoppage.start_date}
              </p>
              <p>
                <strong>Expected:</strong> {selectedStoppage.expected_resumption_date}
              </p>
            </div>
          )}

          <form id="resume-form" onSubmit={handleSubmit}>
            <TextField
              type="date"
              name="actual_resumption_date"
              label="Actual Resumption Date"
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
              value={form.actual_resumption_date}
              onChange={handleChange}
              sx={{ mb: 2 }}
            />

            <TextField
              name="remarks"
              label="Remarks"
              multiline
              rows={3}
              fullWidth
              value={form.remarks}
              onChange={handleChange}
              sx={{ mb: 2 }}
            />

            <input
              type="file"
              onChange={(e) => setFile(e.target.files[0])}
              style={{ marginTop: "8px" }}
            />
          </form>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button type="submit" form="resume-form" variant="contained" color="primary">
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar Feedback */}
      <Snackbar
        open={!!message}
        autoHideDuration={3000}
        onClose={() => setMessage(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setMessage(null)}
          severity={message?.includes("Failed") ? "error" : "success"}
          sx={{ width: "100%" }}
        >
          {message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}
